import { useState, type FormEvent } from "react";

type FormStatus = "idle" | "submitting" | "success" | "error";

const SUBMIT_URL = "https://readdy.ai/api/form/dahhmkrp14a1h3mr6n60";

export default function SubscribeSection() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <section className="mx-auto w-full max-w-[1280px] px-4 pb-16 md:px-6">
      <div className="grid grid-cols-1 items-center gap-8 rounded-lg bg-primary-500 px-6 py-10 md:px-12 md:py-12 lg:grid-cols-[1.1fr_1fr] lg:gap-12">
        <div>
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
            订阅每周话题精选
          </h2>
          <p className="mt-3 max-w-lg text-sm leading-7 text-primary-50">
            每周一封邮件，只做三件事：挑出当周讨论度最高的话题、整理瓜友高赞观点、提醒你还没聊透的后续。
          </p>
          <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-xs text-primary-50">
            <li className="flex items-center gap-1.5">
              <i className="ri-check-line"></i>无广告推销
            </li>
            <li className="flex items-center gap-1.5">
              <i className="ri-check-line"></i>随时可退订
            </li>
            <li className="flex items-center gap-1.5">
              <i className="ri-check-line"></i>每周一封
            </li>
          </ul>
        </div>

        <div className="rounded-lg bg-background-50 p-6">
          {status === "success" ? (
            <div className="flex flex-col items-center py-6 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-mail-check-line text-2xl"></i>
              </span>
              <p className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                订阅成功
              </p>
              <p className="mt-1.5 text-sm text-foreground-600">
                每周话题精选会发到你的邮箱，记得留意收件箱。
              </p>
            </div>
          ) : (
            <form
              id="subscribe-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3"
            >
              <label htmlFor="subscribe-email" className="text-sm font-medium text-foreground-800">
                邮箱地址
              </label>
              <input
                id="subscribe-email"
                type="email"
                name="email"
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
              />

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-accessory-note"
              />

              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    免费订阅
                  </>
                )}
              </button>

              {status === "error" && (
                <p className="rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-700">
                  {formError}
                </p>
              )}

              <p className="text-xs leading-6 text-foreground-500">
                订阅即表示你同意我们通过邮件发送话题精选，我们不会分享你的邮箱给第三方。
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}